/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useState } from 'react';
import PhotoLightbox from '../components/PhotoLightbox';
import { ABOUT_IMAGES, ABOUT_PHOTOS, ABOUT_WTO_REVIEW_URL } from '../data/siteData';

export function AboutCopy({ html, className = '' }) {
  if (!html) {
    return null;
  }

  return <section className={`fsf-about-copy ${className}`.trim()} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function AboutPhoto({ photo, index, onOpen, className = '', loading = 'lazy' }) {
  if (!photo?.src) {
    return null;
  }

  return (
    <figure className={`fsf-about-media ${className}`.trim()}>
      <button
        className="fsf-about-photo-button"
        type="button"
        onClick={() => onOpen(index)}
        aria-label={`Open ${photo.alt}`}
      >
        <img src={photo.src} alt={photo.alt} loading={loading} />
        <span className="fsf-gallery__overlay fsf-about-photo-overlay" aria-hidden="true">
          View Photo
        </span>
      </button>
    </figure>
  );
}

export default function AboutPageSections({ page }) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(null);
  const blocks = page.blocks || [];

  return (
    <>
      <div className="fsf-about-story">
        <AboutCopy html={blocks[0]?.html} className="fsf-about-copy--intro" />

        <section className="fsf-about-wto" aria-label="World Trade Organization review">
          <AboutCopy html={blocks[1]?.html} className="fsf-about-wto__copy" />
          <a
            className="fsf-about-wto__logo"
            href={ABOUT_WTO_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Fiji 1997 Trade Policy Review on the WTO website"
          >
            <img src={ABOUT_IMAGES.wto} alt="World Trade Organization" loading="lazy" />
          </a>
        </section>

        <AboutCopy html={blocks[2]?.html} />

        <section className="fsf-about-media-stack" aria-label="Freight Services Fiji connecting the Pacific">
          <AboutPhoto
            photo={ABOUT_PHOTOS[1]}
            index={1}
            onOpen={setActivePhotoIndex}
            className="fsf-about-media--wide"
            loading="eager"
          />
          <AboutPhoto
            photo={ABOUT_PHOTOS[2]}
            index={2}
            onOpen={setActivePhotoIndex}
            className="fsf-about-media--wide"
          />
        </section>

        <AboutCopy html={blocks[3]?.html} />

        <AboutPhoto
          photo={ABOUT_PHOTOS[3]}
          index={3}
          onOpen={setActivePhotoIndex}
          className="fsf-about-media--wide"
        />

        <AboutCopy html={blocks[4]?.html} className="fsf-about-copy--poem" />
        <AboutCopy html={blocks[5]?.html} className="fsf-about-copy--note" />

        <AboutPhoto
          photo={ABOUT_PHOTOS[4]}
          index={4}
          onOpen={setActivePhotoIndex}
          className="fsf-about-media--wide fsf-about-media--contained"
        />

        <section className="fsf-about-un" aria-label="United Nations Global Compact">
          <AboutPhoto
            photo={ABOUT_PHOTOS[5]}
            index={5}
            onOpen={setActivePhotoIndex}
            className="fsf-about-media--wide fsf-about-media--contained"
          />
          <AboutCopy html={blocks[6]?.html} className="fsf-about-copy--compact" />
        </section>

        <section className="fsf-about-light" aria-label="The Light of the Pacific">
          <AboutPhoto
            photo={ABOUT_PHOTOS[6]}
            index={6}
            onOpen={setActivePhotoIndex}
            className="fsf-about-media--wide"
          />
          <AboutCopy html={blocks[7]?.html} className="fsf-about-copy--light" />
          <AboutPhoto
            photo={ABOUT_PHOTOS[7]}
            index={7}
            onOpen={setActivePhotoIndex}
            className="fsf-about-media--wide"
          />
        </section>
      </div>

      <PhotoLightbox
        images={ABOUT_PHOTOS}
        activeIndex={activePhotoIndex}
        setActiveIndex={setActivePhotoIndex}
        label="Freight Services Fiji about photos"
      />
    </>
  );
}
